import { Modal, Button, Select } from 'antd';
import { useEffect, useState, useRef, useMemo } from 'react';
import { MicrophoneLevel } from '../modules/ambient_solo/components/baseComponents/MicrophoneLevel';
import { MicOn, SettingsMicIcon } from 'src/icons';
import { Progress, Tooltip } from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { Bars } from 'react-loader-spinner';
import AudioPlayer from 'src/components/baseComponents/AudioStream';

const MIC_TEST_DURATION = 30; // seconds
const SAMPLE_RATE = 44100;

function calculateMicLevelPercent(inputData) {
  const rms = Math.sqrt(
    inputData.reduce((sum, sample) => sum + sample * sample, 0) /
      inputData.length
  );
  return Math.min(rms * 200 * 5, 100);
}

function convertChunksToWavBlobUrl(chunks) {
  // Create WAV header
  const sampleRate = SAMPLE_RATE;
  const numChannels = 1;
  const bitsPerSample = 16;
  const dataLength = chunks.reduce((acc, chunk) => acc + chunk.length * 2, 0);

  const header = new ArrayBuffer(44);
  const view = new DataView(header);

  // "RIFF" chunk descriptor
  view.setUint8(0, 'R'.charCodeAt(0));
  view.setUint8(1, 'I'.charCodeAt(0));
  view.setUint8(2, 'F'.charCodeAt(0));
  view.setUint8(3, 'F'.charCodeAt(0));
  view.setUint32(4, 36 + dataLength, true);
  view.setUint8(8, 'W'.charCodeAt(0));
  view.setUint8(9, 'A'.charCodeAt(0));
  view.setUint8(10, 'V'.charCodeAt(0));
  view.setUint8(11, 'E'.charCodeAt(0));

  // "fmt " sub-chunk
  view.setUint8(12, 'f'.charCodeAt(0));
  view.setUint8(13, 'm'.charCodeAt(0));
  view.setUint8(14, 't'.charCodeAt(0));
  view.setUint8(15, ' '.charCodeAt(0));
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * (bitsPerSample / 8), true);
  view.setUint16(32, numChannels * (bitsPerSample / 8), true);
  view.setUint16(34, bitsPerSample, true);

  // "data" sub-chunk
  view.setUint8(36, 'd'.charCodeAt(0));
  view.setUint8(37, 'a'.charCodeAt(0));
  view.setUint8(38, 't'.charCodeAt(0));
  view.setUint8(39, 'a'.charCodeAt(0));
  view.setUint32(40, dataLength, true);
  // Combine header with audio data
  return URL.createObjectURL(
    new Blob([header, ...chunks], { type: 'audio/wav' })
  );
}

const MicSettings = ({ onSelectionChange, selectedMicrophone, disabled }) => {
  const [open, setOpen] = useState(false);
  const [microphones, setMicrophones] = useState([]);
  const [micLevel, setMicLevel] = useState(1);
  const [stream, setStream] = useState(null);

  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(isRecording);
  isRecordingRef.current = isRecording;

  const [recordingTimer, setRecordingTimer] = useState(0);
  const recordingTimerRef = useRef(recordingTimer);
  recordingTimerRef.current = recordingTimer;

  const [recordingChunks, setRecordingChunks] = useState([]);

  const audioUrl = useMemo(() => {
    if (!recordingChunks.length || isRecording) {
      return null;
    }

    return convertChunksToWavBlobUrl(recordingChunks);
  }, [recordingChunks, isRecording]);

  useEffect(() => {
    const getMicrophones = async () => {
      if (!open) {
        return;
      }
      try {
        // Request microphone permission first to get device labels
        await navigator.mediaDevices.getUserMedia({ audio: true });

        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputDevices = devices.filter(
          (device) => device.kind === 'audioinput'
        );
        setMicrophones(audioInputDevices);
        if (
          !audioInputDevices.some(
            (device) => device.deviceId === selectedMicrophone
          )
        ) {
          onSelectionChange(audioInputDevices[0].deviceId);
        }
      } catch (error) {
        console.error('Error accessing microphones:', error);
      }
    };

    getMicrophones();
    navigator.mediaDevices.addEventListener('devicechange', getMicrophones);
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        getMicrophones
      );
    };
  }, [onSelectionChange, open, selectedMicrophone]);

  useEffect(() => {
    const enableMicStream = async () => {
      const constraints = {
        audio: {
          deviceId: { exact: selectedMicrophone },
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(stream);
    };

    if (selectedMicrophone && open) {
      enableMicStream();
    } else {
      setStream(null);
    }
  }, [selectedMicrophone, open]);

  useEffect(() => {
    const streamLocal = stream;
    let audioContext;
    let scriptNode;
    let input;

    const processAudioEvent = (event) => {
      const inputData = event.inputBuffer.getChannelData(0);
      setMicLevel(calculateMicLevelPercent(inputData));

      if (isRecordingRef.current) {
        // Convert float32 audio data to PCM16LE format
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          // Clamp the value between -1 and 1, then scale to 16-bit range
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        setRecordingChunks((chunks) => [...chunks, pcmData]);
      }
    };

    if (streamLocal) {
      // Create an AudioContext
      audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: SAMPLE_RATE,
      });

      // Set up the audio processing nodes
      /**
       * Not passing the buffer size to the script processor for it to auto-calculate the best suitable
       * https://developer.mozilla.org/en-US/docs/Web/API/BaseAudioContext/createScriptProcessor#buffersize
       */
      scriptNode = audioContext.createScriptProcessor(0, 1, 1);
      input = audioContext.createMediaStreamSource(streamLocal); // stream is your MediaStream

      // Connect the nodes
      input.connect(scriptNode);
      scriptNode.connect(audioContext.destination);

      // Process the audio
      scriptNode.addEventListener('audioprocess', processAudioEvent);

      // Resume the audio context
      audioContext.resume();
    }

    return () => {
      if (scriptNode) {
        scriptNode.removeEventListener('audioprocess', processAudioEvent);
        scriptNode.disconnect();
      }

      // Disconnect input
      if (input) {
        input.disconnect();
      }

      // Stop all tracks in the stream
      if (streamLocal) {
        streamLocal.getTracks().forEach((track) => track.stop());
      }

      // Close audio context if it exists and isn't already closed
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close();
      }
    };
  }, [stream]);

  useEffect(() => {
    let intervalId;
    if (isRecording) {
      intervalId = setInterval(() => {
        setRecordingTimer((prev) => prev + 1);
        if (recordingTimerRef.current >= MIC_TEST_DURATION) {
          setIsRecording(false);
        }
      }, 1000);
    } else {
      setRecordingTimer(0);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [isRecording]);

  const renderMicTest = () => {
    if (audioUrl) {
      return (
        <div className="flex items-center gap-2 mt-4">
          <AudioPlayer audioUrl={audioUrl} />
          <Tooltip title="Reset" placement="bottom">
            <CloseOutlined
              className="cursor-pointer"
              onClick={() => {
                setIsRecording(false);
                setRecordingChunks([]);
              }}
            />
          </Tooltip>
        </div>
      );
    }

    if (isRecording) {
      return (
        <div className="flex flex-col justify-center items-center gap-2 mt-4">
          <Progress
            type="circle"
            strokeColor="#00d090"
            percent={(recordingTimer / MIC_TEST_DURATION) * 100}
            size={80}
            format={() => (
              <div className="flex flex-col justify-center items-center">
                <p className="text-gray-500 text-lg">{recordingTimer}s</p>
                <Bars height="8" width="8" color="#00d090" />
              </div>
            )}
          />
          <div className="flex gap-3 items-center">
            <Tooltip title="Play" placement="bottom">
              <CheckOutlined
                className="cursor-pointer"
                onClick={() => {
                  setIsRecording(false);
                }}
              />
            </Tooltip>
            <Tooltip title="Stop recording" placement="bottom">
              <CloseOutlined
                className="cursor-pointer"
                onClick={() => {
                  setIsRecording(false);
                  setRecordingChunks([]);
                }}
              />
            </Tooltip>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <SettingsMicIcon disabled={disabled} onClick={() => setOpen(true)} />
      <Modal
        title="Microphone Settings"
        centered={true}
        footer={
          <div className="flex justify-end gap-2 mt-8">
            {!isRecording && !audioUrl && (
              <Button
                className="ih-button-primary left-0 mr-auto"
                onClick={() => setIsRecording(true)}
              >
                Test microphone
              </Button>
            )}
            <Button
              className="ih-button-secondary"
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
          </div>
        }
        open={open}
        onCancel={() => setOpen(false)}
        afterOpenChange={setOpen}
      >
        <div className="flex flex-col gap-4">
          <Select
            disabled={isRecording}
            loading={microphones.length === 0}
            value={selectedMicrophone}
            onChange={(deviceId) => onSelectionChange(deviceId)}
          >
            {microphones.map((microphone) => (
              <Select.Option
                key={microphone.deviceId}
                value={microphone.deviceId}
              >
                {microphone.label || `Microphone ${microphone.deviceId}`}
              </Select.Option>
            ))}
          </Select>
          <div className="flex items-center gap-2">
            <MicOn />
            <MicrophoneLevel micLevel={micLevel} />
          </div>
        </div>
        {renderMicTest()}
      </Modal>
    </>
  );
};

export { MicSettings };
